import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  OnboardingDialog,
  incomeOnboardingSchemas,
  INCOME_ONBOARDING_STEP_OFFSET,
} from "@/pages/private/noNavButFooter/UpdateBudgetPageCom";
import { useAlert } from "@/app/providers/AlertProvider";
import { useForm, useUI } from "@/shared/state/store";
import { BudgetService, ProfileService, OnboardingService, DependentsService } from "@/shared/api/services";
import type { DependentDobRecord } from "@/shared/api/services/dependentsService";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography } from "@mui/material";
import { calculateAge, parseDobToDate } from "@/shared/lib/utils/others";
import {
  computeConfirmBeforeClearPlan,
  type BudgetSnapshots,
  type ProfileBudgetContext,
} from "@/shared/budget/confirmBeforeClear";

type ScreenSchema = Parameters<typeof OnboardingDialog>[0]["currentSchema"];

interface IncomeOnboardingOverlayProps {
  open: boolean;
  onComplete: () => void;
  onCancel: () => void;
  /**
   * Optional flag to force start from step 0 even if stage API returns later step.
   */
  forceRestart?: boolean;
}

/**
 * Reusable overlay for the income onboarding flow (second-level onboarding).
 * - Fetches customer profile to prefill steps.
 * - Persists progress to CustomerFieldStage via OnboardingService.
 * - Emits completion when the final step is saved.
 */
const IncomeOnboardingOverlay: React.FC<IncomeOnboardingOverlayProps> = ({
  open,
  onComplete,
  onCancel,
  forceRestart = false,
}) => {
  const navigate = useNavigate();
  const { setFormData, formData: allFormData } = useForm();
  const { setLoading, loading } = useUI();
  const { showAlert } = useAlert();

  // Confirmation dialog: used when a profile change will hide valued budget fields
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPendingAction, setConfirmPendingAction] = useState<
    null | (() => Promise<void>)
  >(null);
  const [confirmMessage, setConfirmMessage] = useState<string>(
    "This change will remove some saved budget data. Do you want to continue?"
  );

  // Helper: store an async callback in state without it being treated as a state-updater
  const setPendingConfirmAction = useCallback((action: () => Promise<void>): void => {
    setConfirmPendingAction(() => action);
  }, []);

  const [currentStep, setCurrentStep] = useState(0);
  const [formInstanceKeys, setFormInstanceKeys] = useState<Record<number, number>>({});
  const [dependentsDobData, setDependentsDobData] = useState<DependentDobRecord[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const liveStepValuesRef = useRef<Record<string, unknown>>({});

  const currentSchema: ScreenSchema = useMemo(
    () => (incomeOnboardingSchemas[currentStep] as ScreenSchema) ?? ({} as ScreenSchema),
    [currentStep]
  );

  const screenRenderKey = `${currentStep}-${formInstanceKeys[currentStep] ?? 0}`;
  const totalSteps = incomeOnboardingSchemas.length;

  // Field-stage tracking
  const fieldStageCustomerIdRef = useRef<number | null>(null);
  const lastPersistedStageRef = useRef<string | null>(null);

  const bumpFormInstanceKey = useCallback((step: number) => {
    setFormInstanceKeys((prev) => ({ ...prev, [step]: (prev[step] ?? 0) + 1 }));
  }, []);

  const resolveUserId = useCallback((): number | null => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem("userData") : null;
      const parsed = raw ? JSON.parse(raw) : null;
      return typeof parsed?.userId === "number" ? parsed.userId : null;
    } catch {
      return null;
    }
  }, []);

  const resolveCustomerId = useCallback((): number | null => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem("customerData") : null;
      const parsed = raw ? JSON.parse(raw) : null;
      const id = parsed?.customerId ?? parsed?.id;
      return typeof id === "number" ? id : null;
    } catch {
      return null;
    }
  }, []);

  const ensureFieldStageCustomerId = useCallback(async (): Promise<number | null> => {
    if (typeof fieldStageCustomerIdRef.current === "number") return fieldStageCustomerIdRef.current;
    const cached = resolveCustomerId();
    if (cached) {
      fieldStageCustomerIdRef.current = cached;
      return cached;
    }
    const userId = resolveUserId();
    if (!userId) return null;
    try {
      const resp = await OnboardingService.getCustomerByUserId(userId, { skipErrorCapture: true });
      const derived = resp?.data?.customerId ?? resp?.data?.id;
      if (typeof derived === "number") {
        fieldStageCustomerIdRef.current = derived;
        try {
          localStorage.setItem("customerData", JSON.stringify(resp.data));
        } catch {
          /* ignore */
        }
        return derived;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, [resolveCustomerId, resolveUserId]);

  const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  };

  const readStoredCustomerData = useCallback((): Record<string, unknown> => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem("customerData") : null;
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }, []);

  const getStringFromRecord = (obj: Record<string, unknown>, key: string): string | undefined => {
    const v = obj[key];
    return typeof v === "string" ? v : undefined;
  };

  const coerceToInt = (value: unknown): number | undefined => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const n = parseInt(value, 10);
      return Number.isNaN(n) ? undefined : n;
    }
    return undefined;
  };

  const computeAgeSafe = (dobStr: string | undefined): number | undefined => {
    if (!dobStr) return undefined;
    try {
      const age = calculateAge(dobStr);
      return typeof age === "number" && Number.isFinite(age) ? age : undefined;
    } catch {
      return undefined;
    }
  };

  const fetchBudgetSnapshotsFresh = useCallback(
    async (customerId: number): Promise<BudgetSnapshots> => {
      try {
        const [incomeResp, incomeNotesResp, expenseResp, expenseNotesResp] =
          await Promise.all([
            BudgetService.getIncomeData(customerId, { skipCache: true }),
            BudgetService.getIncomeNotes(customerId, { skipCache: true }),
            BudgetService.getExpenseData(customerId, { skipCache: true }),
            BudgetService.getExpenseNotes(customerId, { skipCache: true }),
          ]);

        const income =
          incomeResp?.success && Array.isArray(incomeResp.data) && incomeResp.data[0]
            ? incomeResp.data[0]
            : null;
        const incomeNotes =
          incomeNotesResp?.success &&
          Array.isArray(incomeNotesResp.data) &&
          incomeNotesResp.data[0]
            ? incomeNotesResp.data[0]
            : null;
        const expenses =
          expenseResp?.success && Array.isArray(expenseResp.data) && expenseResp.data[0]
            ? expenseResp.data[0]
            : null;
        const expenseNotes =
          expenseNotesResp?.success &&
          Array.isArray(expenseNotesResp.data) &&
          expenseNotesResp.data[0]
            ? expenseNotesResp.data[0]
            : null;

        return { income, incomeNotes, expenses, expenseNotes };
      } catch {
        return { income: null, incomeNotes: null, expenses: null, expenseNotes: null };
      }
    },
    []
  );

  const buildProfileBudgetContext = useCallback(
    (overrides: Partial<ProfileBudgetContext> = {}): ProfileBudgetContext => {
      const stored = readStoredCustomerData();
      const customerEmploymentStatus = getStringFromRecord(stored, "customerEmploymentStatus");
      const partnerEmploymentStatus = getStringFromRecord(stored, "partnerEmploymentStatus");
      const jointAccount = getStringFromRecord(stored, "jointAccount");
      const accommodationType =
        getStringFromRecord(stored, "housingTenure") ?? getStringFromRecord(stored, "accommodationType");
      const haveChildrenUnder18 = getStringFromRecord(stored, "haveChildrenUnder18");
      const dependentChildrenUnder16 = coerceToInt(stored["dependentChildrenUnder16"]);
      const dependentChildrenAged16Plus = coerceToInt(stored["dependentChildrenAged16Plus"]);

      const customerDob =
        getStringFromRecord(stored, "debtor1DateOfBirthStr") ??
        getStringFromRecord(stored, "dateOfBirth") ??
        getStringFromRecord(stored, "debtor1DateOfBirth");
      const partnerDob =
        getStringFromRecord(stored, "debtor2DateOfBirthStr") ??
        getStringFromRecord(stored, "debtor2DateOfBirth") ??
        getStringFromRecord(stored, "partnerDateOfBirth");

      const customerAge = computeAgeSafe(customerDob);
      const partnerAge = computeAgeSafe(partnerDob);

      return {
        customerEmploymentStatus,
        partnerEmploymentStatus,
        jointAccount,
        accommodationType,
        dependentChildrenUnder16,
        dependentChildrenAged16Plus,
        haveChildrenUnder18,
        customerAge,
        partnerAge,
        ...overrides,
      };
    },
    [readStoredCustomerData]
  );

  const persistFieldStage = useCallback(
    async (stageIndex: number) => {
      const customerId = await ensureFieldStageCustomerId();
      const userId = resolveUserId();
      if (!customerId || !userId) return;
      const stageStr = String(stageIndex + INCOME_ONBOARDING_STEP_OFFSET);
      if (lastPersistedStageRef.current === stageStr) return;
      try {
        const resp = await OnboardingService.upsertCustomerFieldStage(
          {
            customerId,
            fieldStage: stageStr,
            userId,
          },
          { skipErrorCapture: true }
        );
        if (resp && typeof resp === "object") {
          lastPersistedStageRef.current = stageStr;
        }
      } catch {
        /* ignore */
      }
    },
    [ensureFieldStageCustomerId, resolveUserId]
  );

  const prefillFromProfile = useCallback(async () => {
    const userId = resolveUserId();
    if (!userId) return;

    setLoading("income-onboarding-prefill", true);
    try {
      const response = await ProfileService.getCustomerData(userId, {
        skipCache: true,
        skipErrorCapture: true,
      });

      if (response.success && response.data) {
        const customer = (Array.isArray(response.data) ? response.data[0] : response.data) as any;

        // Step 0
        const step0: Record<string, unknown> = {};
        if (customer.housingTenure) step0.accomodationType = customer.housingTenure;
        setFormData("income-onboarding-step-0", step0);

        // Step 1
        const step1: Record<string, unknown> = {};
        if (customer.customerEmploymentStatus) {
          step1.customerEmploymentStatus = customer.customerEmploymentStatus;
        }
        setFormData("income-onboarding-step-1", step1);

        // Step 2
        const step2: Record<string, unknown> = {};
        if (customer.jointAccount) {
          step2.budgetWithPartner = customer.jointAccount === "Y";
          if (customer.jointAccount === "Y") {
            if (customer.debtor2Name) step2.name = customer.debtor2Name;
            const dobRaw = customer.debtor2DateOfBirthStr || customer.debtor2DateOfBirth;
            if (dobRaw) {
              const dobDate = new Date(dobRaw);
              if (!Number.isNaN(dobDate.getTime())) step2.dob = dobDate;
            }
            if (customer.partnerEmploymentStatus) {
              step2.employmentStatus = customer.partnerEmploymentStatus;
            }
          }
        }
        setFormData("income-onboarding-step-2", step2);

        // Step 3
        const step3: Record<string, unknown> = {};
        if (customer.jointAccount) {
          step3.budgetWithPartner = customer.jointAccount === "Y";
        }
        
        // Only pre-fill haveDependents if the user has previously answered this question
        // Check if haveChildrenUnder18 exists (has been set before)
        const hasExistingDependentsAnswer = customer.haveChildrenUnder18 !== undefined && customer.haveChildrenUnder18 !== null;
        
        if (hasExistingDependentsAnswer) {
          // adultsOver18IncludingMyself is actually other adults excluding customer and partner
          const otherAdultsOver18 = typeof customer.adultsOver18IncludingMyself === "number"
            ? customer.adultsOver18IncludingMyself
            : 0;
          
          // haveDependents is true if there are children under 18 OR other adults
          const hasChildren = customer.haveChildrenUnder18 === "Y";
          const hasOtherAdults = otherAdultsOver18 > 0;
          const haveDependents = hasChildren || hasOtherAdults;
          
          step3.haveDependents = haveDependents;
          if (haveDependents) {
            step3.under16Dependents = customer.dependentChildrenUnder16 ?? 0;
            step3.between16to18Dependents = customer.dependentChildrenAged16Plus ?? 0;
            step3.otherAdultsOver18 = otherAdultsOver18;
            try {
              const deps = await DependentsService.getDependents(customer.customerId);
              if (deps.success && deps.data) {
                setDependentsDobData(deps.data);
                const under16 = deps.data
                  .filter((d) => d.dependantCategory === "age-under16")
                  .sort((a, b) => Number(a.childDependantNo) - Number(b.childDependantNo));
                const between16 = deps.data
                  .filter((d) => d.dependantCategory === "age-16to18")
                  .sort((a, b) => Number(a.childDependantNo) - Number(b.childDependantNo));
                under16.forEach((dep) => {
                  if (dep.childDependantDobStr) {
                    step3[`under16DependentsDOB_${dep.childDependantNo}`] = new Date(
                      dep.childDependantDobStr
                    );
                  }
                });
                between16.forEach((dep) => {
                  if (dep.childDependantDobStr) {
                    step3[`between16to18DependentsDOB_${dep.childDependantNo}`] = new Date(
                      dep.childDependantDobStr
                    );
                  }
                });
              }
            } catch {
              /* ignore dob fetch errors */
            }
          }
        }
        setFormData("income-onboarding-step-3", step3);

        // Step 4
        const step4: Record<string, unknown> = {};
        if (customer.haveVehicle) {
          step4.haveVehicle = customer.haveVehicle === "Y";
          if (customer.haveVehicle === "Y" && customer.noOfVehiclesInHouseholds !== undefined) {
            step4.noOfVehiclesInHouseholds = customer.noOfVehiclesInHouseholds;
          }
        }
        setFormData("income-onboarding-step-4", step4);

        for (let i = 0; i < totalSteps; i++) bumpFormInstanceKey(i);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to prefill income onboarding", error);
    } finally {
      setLoading("income-onboarding-prefill", false);
    }
  }, [resolveUserId, setFormData, bumpFormInstanceKey, totalSteps, setLoading]);

  const handleSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      const formData = data;
      const formId = `income-onboarding-step-${currentStep}`;
      setFormData(formId, formData);

      const customerId = await ensureFieldStageCustomerId();
      const userId = resolveUserId();
      if (!customerId || !userId) {
        showAlert({
          severity: "error",
          message: "Missing customer or user id",
          autoHide: true,
          duration: 3000,
        });
        return;
      }

      const payload: Record<string, unknown> = {};

      switch (currentStep) {
        case 0:
          if (typeof formData.accomodationType === "string" && formData.accomodationType) {
            payload.housingTenure = formData.accomodationType;
          }
          break;
        case 1:
          if (
            typeof formData.customerEmploymentStatus === "string" &&
            formData.customerEmploymentStatus
          ) {
            payload.customerEmploymentStatus = formData.customerEmploymentStatus;
          }
          break;
        case 2:
          if (formData.budgetWithPartner !== undefined) {
            payload.jointAccount = formData.budgetWithPartner === true ? "Y" : "N";
            if (formData.budgetWithPartner === true) {
              if (typeof formData.name === "string" && formData.name) payload.debtor2Name = formData.name;
              if (formData.dob) {
                // parseDobToDate treats YYYY-MM-DD as a local date — `new Date("YYYY-MM-DD")`
                // parses as UTC midnight and shifts the day in negative UTC offsets, so the
                // payload would carry a DOB one day earlier than the user picked.
                const dobDate =
                  formData.dob instanceof Date ? formData.dob : parseDobToDate(String(formData.dob));
                if (dobDate && !Number.isNaN(dobDate.getTime())) {
                  const y = dobDate.getFullYear();
                  const m = String(dobDate.getMonth() + 1).padStart(2, "0");
                  const d = String(dobDate.getDate()).padStart(2, "0");
                  const dobStr = `${y}-${m}-${d}`;
                  payload.debtor2DateOfBirthStr = dobStr;
                  payload.debtor2DateOfBirth = dobStr;
                }
              }
              if (typeof formData.employmentStatus === "string" && formData.employmentStatus) {
                payload.partnerEmploymentStatus = formData.employmentStatus;
              }
            }
          }
          break;
        case 3: {
          const hasDependents = formData.haveDependents === true;
          if (formData.haveDependents !== undefined) {
            payload.haveChildrenUnder18 = hasDependents ? "Y" : "N";
          }
          if (hasDependents) {
            if (formData.under16Dependents !== undefined) {
              payload.dependentChildrenUnder16 = formData.under16Dependents;
            }
            if (formData.between16to18Dependents !== undefined) {
              payload.dependentChildrenAged16Plus = formData.between16to18Dependents;
            }
            if (formData.otherAdultsOver18 !== undefined) {
              // adultsOver18IncludingMyself is actually other adults excluding customer and partner
              payload.adultsOver18IncludingMyself = formData.otherAdultsOver18;
            }
          } else {
            payload.dependentChildrenUnder16 = 0;
            payload.dependentChildrenAged16Plus = 0;
            payload.adultsOver18IncludingMyself = 0;
          }
          break;
        }
        case 4:
          if (formData.haveVehicle !== undefined) {
            payload.haveVehicle = formData.haveVehicle === true ? "Y" : "N";
            if (formData.haveVehicle === true && formData.noOfVehiclesInHouseholds !== undefined) {
              payload.noOfVehiclesInHouseholds = formData.noOfVehiclesInHouseholds;
            }
          }
          break;
        default:
          break;
      }

      const clearBudgetForPlan = async (
        plan: ReturnType<typeof computeConfirmBeforeClearPlan>
      ): Promise<void> => {
        try {
          if (plan.clearPayloads.incomeData) {
            await BudgetService.updateIncomeData(plan.clearPayloads.incomeData);
          }
          if (plan.clearPayloads.incomeNotes) {
            await BudgetService.updateIncomeNotes(plan.clearPayloads.incomeNotes);
          }
          if (plan.clearPayloads.expenseData) {
            await BudgetService.updateExpenseData(plan.clearPayloads.expenseData);
          }
          if (plan.clearPayloads.expenseNotes) {
            await BudgetService.updateExpenseNotes(plan.clearPayloads.expenseNotes);
          }
        } catch {
          // Non-blocking
        }
      };

      const doSave = async (): Promise<void> => {
        try {
          setLoading("income-onboarding-submit", true);

          if (Object.keys(payload).length > 0) {
            const resp = await ProfileService.patchCustomerData(customerId, payload);
            if (!resp.success) {
              const message =
                typeof resp.error === "string"
                  ? resp.error
                  : resp.error?.message || "Failed to update profile details";
              throw new Error(message);
            }

            try {
              const raw = localStorage.getItem("customerData");
              const parsed = raw ? JSON.parse(raw) : {};
              localStorage.setItem("customerData", JSON.stringify({ ...parsed, ...payload }));
            } catch {
              /* ignore */
            }
          }

          await persistFieldStage(currentStep);

          if (currentStep < totalSteps - 1) {
            if (currentStep === 2 && formData.budgetWithPartner !== undefined) {
              const step3Raw = allFormData["income-onboarding-step-3"];
              const step3Existing: Record<string, unknown> =
                isRecord(step3Raw) ? step3Raw : {};
              const jointAccount = formData.budgetWithPartner === true ? "Y" : "N";
              setFormData("income-onboarding-step-3", {
                ...step3Existing,
                budgetWithPartner: formData.budgetWithPartner,
                jointAccount,
              });
            }
            setCurrentStep((s) => s + 1);
            bumpFormInstanceKey(currentStep + 1);
          } else {
            await persistFieldStage(totalSteps);
            showAlert({
              severity: "success",
              message: "Onboarding completed successfully!",
              autoHide: true,
              duration: 2000,
            });
            onComplete();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to submit income onboarding", error);
          showAlert({
            severity: "error",
            message:
              error instanceof Error ? error.message : "Failed to save progress. Please try again.",
            autoHide: true,
            duration: 3000,
          });
        } finally {
          setLoading("income-onboarding-submit", false);
        }
      };

      // Confirm-before-clear gating (clear budget first, then patch profile)
      try {
        const before = buildProfileBudgetContext();

        const afterOverrides: Partial<ProfileBudgetContext> = {};
        const nextEmployment = payload["customerEmploymentStatus"];
        if (typeof nextEmployment === "string") afterOverrides.customerEmploymentStatus = nextEmployment;
        const nextPartnerEmployment = payload["partnerEmploymentStatus"];
        if (typeof nextPartnerEmployment === "string")
          afterOverrides.partnerEmploymentStatus = nextPartnerEmployment;
        const nextJoint = payload["jointAccount"];
        if (typeof nextJoint === "string") afterOverrides.jointAccount = nextJoint;
        const nextAccommodation = payload["housingTenure"];
        if (typeof nextAccommodation === "string") afterOverrides.accommodationType = nextAccommodation;
        const nextHaveChildren = payload["haveChildrenUnder18"];
        if (typeof nextHaveChildren === "string") afterOverrides.haveChildrenUnder18 = nextHaveChildren;
        const nextUnder16 = payload["dependentChildrenUnder16"];
        if (typeof nextUnder16 === "number") afterOverrides.dependentChildrenUnder16 = nextUnder16;
        const next16Plus = payload["dependentChildrenAged16Plus"];
        if (typeof next16Plus === "number") afterOverrides.dependentChildrenAged16Plus = next16Plus;

        const dobStrCandidate = payload["debtor2DateOfBirthStr"];
        const dobCandidate = payload["debtor2DateOfBirth"];
        const nextPartnerDob =
          typeof dobStrCandidate === "string"
            ? dobStrCandidate
            : typeof dobCandidate === "string"
              ? dobCandidate
              : undefined;
        if (typeof nextPartnerDob === "string") {
          afterOverrides.partnerAge = computeAgeSafe(nextPartnerDob);
        }

        const after: ProfileBudgetContext = { ...before, ...afterOverrides };
        const snaps = await fetchBudgetSnapshotsFresh(customerId);
        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId,
        });

        if (plan.needsConfirm) {
          setConfirmMessage(
            `This change will remove the data you entered for: ${plan.clearedLabels.join(
              ", "
            )}. Do you want to continue?`
          );
          setPendingConfirmAction(async () => {
            await clearBudgetForPlan(plan);
            await doSave();
          });
          setConfirmOpen(true);
          return;
        }
      } catch {
        // If gating fails, fall back to saving
      }

      await doSave();
    },
    [
      currentStep,
      setFormData,
      setLoading,
      persistFieldStage,
      totalSteps,
      bumpFormInstanceKey,
      onComplete,
      ensureFieldStageCustomerId,
      resolveUserId,
      showAlert,
      allFormData,
      buildProfileBudgetContext,
      fetchBudgetSnapshotsFresh,
      computeAgeSafe,
    ]
  );

  // Determine start step via field stage
  useEffect(() => {
    if (!open || isInitialized) return;
    const init = async () => {
      setIsInitialized(true);
      await prefillFromProfile();

      let apiStep: number | null = null;
      try {
        const customerId = await ensureFieldStageCustomerId();
        if (customerId) {
          const stageResp = await OnboardingService.getCustomerFieldStage(customerId, {
            skipErrorCapture: true,
          });
          if (stageResp?.fieldStage) {
            const parsed = parseInt(stageResp.fieldStage, 10);
            const base = INCOME_ONBOARDING_STEP_OFFSET;
            if (!Number.isNaN(parsed)) {
              apiStep = parsed <= base ? 0 : parsed - base;
              lastPersistedStageRef.current = stageResp.fieldStage;
            }
          }
        }
      } catch {
        /* ignore */
      }

      const clamp = (n: number) => Math.max(0, Math.min(n, totalSteps - 1));
      const initial = forceRestart ? 0 : apiStep ?? 0;
      setCurrentStep(clamp(initial));
      // If already completed, immediately finish
      const completed = apiStep !== null && apiStep >= totalSteps;
      if (completed && !forceRestart) {
        onComplete();
      }
    };
    void init();
  }, [open, isInitialized, prefillFromProfile, forceRestart, totalSteps, onComplete, ensureFieldStageCustomerId]);

  // Sync step to URL (for parity with budget page) without pushing history
  useEffect(() => {
    if (!open) return;
    try {
      const params = new URLSearchParams(window.location.search);
      params.set("step", String(currentStep));
      navigate({ search: params.toString() }, { replace: true });
    } catch {
      /* ignore */
    }
  }, [currentStep, open, navigate]);

  if (!open) return null;

  return (
    <>
      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmPendingAction(null);
          setConfirmMessage(
            "This change will remove some saved budget data. Do you want to continue?"
          );
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#003d34" }}>
          Confirm change
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">{confirmMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              setConfirmMessage(
                "This change will remove some saved budget data. Do you want to continue?"
              );
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const fn = confirmPendingAction;
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              setConfirmMessage(
                "This change will remove some saved budget data. Do you want to continue?"
              );
              if (typeof fn === "function") {
                await fn();
              }
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <OnboardingDialog
        isOpen={open}
        currentStep={currentStep}
        totalSteps={totalSteps}
        currentSchema={currentSchema}
        screenRenderKey={screenRenderKey}
        formData={allFormData[`income-onboarding-step-${currentStep}`] || {}}
        isLoading={
          Boolean(loading?.["income-onboarding-submit"]) ||
          Boolean(loading?.["income-onboarding-prefill"])
        }
        computedPrimaryText={
          currentStep === totalSteps - 1
            ? "Complete"
            : currentSchema?.actions?.buttons?.primaryText ||
              currentSchema?.actions?.nextButtonText ||
              "Continue"
        }
        shouldShowSecondary={false}
        secondaryButtonText={currentSchema?.actions?.buttons?.secondaryText || "Skip & continue"}
        onClose={onCancel}
        onHelpOpen={() => {
          /* noop for now */
        }}
        onSubmit={handleSubmit}
        onValuesChange={(vals) => {
          liveStepValuesRef.current = vals as Record<string, unknown>;
        }}
        onBack={() => {
          if (currentStep > 0) {
            const prev = currentStep - 1;
            setCurrentStep(prev);
            bumpFormInstanceKey(prev);
          } else {
            onCancel();
          }
        }}
        onSkip={() => {
          if (currentStep < totalSteps - 1) {
            const next = currentStep + 1;
            setCurrentStep(next);
            bumpFormInstanceKey(next);
          } else {
            onCancel();
          }
        }}
        onWarningAction={() => false}
      />
    </>
  );
};

export default IncomeOnboardingOverlay;

